import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { CommissionLine } from '@crm/shared';
import { extractApiError } from '../../lib/api';
import { useAuth } from '../auth/AuthContext';
import { useUserDirectory } from '../schedules/api';
import { downloadCommissionsCsv, useCommissionCalculation } from './api';

function currentMonthIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatPln(value: string): string {
  const n = Number(value);
  return new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: 'PLN',
    maximumFractionDigits: 2,
  }).format(n);
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function CommissionPayoutsPage(): ReactNode {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  // Admin może wybrać dowolnego pracownika; non-admin — tylko siebie.
  const [selectedUserId, setSelectedUserId] = useState<number | null>(
    user ? user.id : null,
  );
  const [month, setMonth] = useState<string>(currentMonthIso());
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const employeesQuery = useUserDirectory();
  const employees = useMemo(
    () => (employeesQuery.data ?? []).filter((u) => u.bookable && u.active),
    [employeesQuery.data],
  );

  // Pre-fill: admin domyślnie pokazuje pierwszego dostępnego pracownika, jeśli
  // jeszcze nie wybrano. Non-admin: zawsze siebie (RBAC backend tak wymusi).
  useEffect(() => {
    if (isAdmin && selectedUserId === null && employees.length > 0) {
      setSelectedUserId(employees[0]!.id);
    }
  }, [isAdmin, employees, selectedUserId]);

  const calc = useCommissionCalculation(selectedUserId, month);

  const onExport = async () => {
    if (selectedUserId === null) return;
    setExporting(true);
    setExportError(null);
    try {
      await downloadCommissionsCsv(selectedUserId, month);
    } catch (err) {
      setExportError(extractApiError(err));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">
            {t('commissions.payouts.title')}
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            {t('commissions.payouts.subtitle')}
          </p>
        </div>
        <button
          type="button"
          onClick={onExport}
          disabled={exporting || selectedUserId === null}
          className="rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50 disabled:opacity-60"
        >
          {exporting ? t('commissions.payouts.exporting') : t('commissions.payouts.exportCsv')}
        </button>
      </header>

      {exportError && (
        <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-700">
          {exportError}
        </div>
      )}

      {/* Filtry */}
      <section className="flex flex-wrap items-end gap-4 rounded-xl bg-white p-4 shadow-sm ring-1 ring-zinc-200">
        {isAdmin && (
          <div>
            <label className="mb-1 block text-sm font-medium text-zinc-700">
              {t('commissions.payouts.employee')}
            </label>
            <select
              value={selectedUserId ?? ''}
              onChange={(e) =>
                setSelectedUserId(e.target.value ? Number(e.target.value) : null)
              }
              className="input min-w-[220px]"
            >
              <option value="">{t('commissions.payouts.selectEmployee')}</option>
              {employees.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.firstName} {u.lastName}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="mb-1 block text-sm font-medium text-zinc-700">
            {t('commissions.payouts.month')}
          </label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="input"
          />
        </div>
      </section>

      {calc.isLoading && (
        <div className="rounded-md border border-zinc-200 bg-white p-6 text-sm text-zinc-500">
          {t('common.loading')}
        </div>
      )}

      {calc.error && (
        <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-700">
          {extractApiError(calc.error)}
        </div>
      )}

      {calc.data && (
        <>
          {calc.data.inProgress && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
              {t('commissions.payouts.inProgressBanner')}
            </div>
          )}

          {/* KPI */}
          <section className="grid gap-4 sm:grid-cols-3">
            <KpiCard
              label={t('commissions.payouts.kpi.appointments')}
              value={String(calc.data.summary.appointmentsCount)}
            />
            <KpiCard
              label={t('commissions.payouts.kpi.revenue')}
              value={formatPln(calc.data.summary.totalRevenue)}
            />
            <KpiCard
              label={t('commissions.payouts.kpi.commission')}
              value={formatPln(calc.data.summary.totalCommission)}
              highlight
            />
          </section>

          {/* Warnings */}
          {calc.data.warnings.length > 0 && (
            <section className="rounded-md border border-amber-300 bg-amber-50 p-3">
              <div className="mb-2 text-sm font-semibold text-amber-900">
                {t('commissions.payouts.warnings.title', {
                  count: calc.data.warnings.length,
                })}
              </div>
              <ul className="space-y-1 text-sm text-amber-800">
                {calc.data.warnings.map((w, idx) => (
                  <li key={`${w.appointmentId}-${w.appointmentServiceId}-${idx}`}>
                    • {w.message}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Tabela wizyt */}
          <section className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-zinc-200">
            <h2 className="mb-3 text-base font-semibold text-zinc-900">
              {t('commissions.payouts.detailsTitle')}
            </h2>
            {calc.data.items.length === 0 ? (
              <div className="p-6 text-sm text-zinc-500">
                {t('commissions.payouts.empty')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wide text-zinc-500">
                      <th className="py-2 pr-3">{t('commissions.payouts.table.date')}</th>
                      <th className="py-2 pr-3">{t('commissions.payouts.table.client')}</th>
                      <th className="py-2 pr-3">{t('commissions.payouts.table.service')}</th>
                      <th className="py-2 pr-3 text-right">
                        {t('commissions.payouts.table.price')}
                      </th>
                      <th className="py-2 pr-3 text-right">
                        {t('commissions.payouts.table.percent')}
                      </th>
                      <th className="py-2 pr-3 text-right">
                        {t('commissions.payouts.table.commission')}
                      </th>
                      <th className="py-2 pr-3">{t('commissions.payouts.table.ruleScope')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calc.data.items.map((entry) =>
                      entry.lines.map((line, idx) => (
                        <tr
                          key={`${entry.appointmentId}-${line.appointmentServiceId}`}
                          className="border-b border-zinc-100 last:border-0"
                        >
                          {idx === 0 ? (
                            <>
                              <td
                                className="py-2 pr-3 align-top"
                                rowSpan={entry.lines.length}
                              >
                                <div className="font-medium text-zinc-900">
                                  {formatDate(entry.startAt)}
                                </div>
                                <div className="text-xs text-zinc-500">
                                  {formatTime(entry.startAt)}
                                </div>
                              </td>
                              <td
                                className="py-2 pr-3 align-top"
                                rowSpan={entry.lines.length}
                              >
                                {entry.client.firstName}
                                {entry.client.lastName ? ` ${entry.client.lastName}` : ''}
                              </td>
                            </>
                          ) : null}
                          <td className="py-2 pr-3">{line.serviceName}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">
                            {formatPln(line.price)}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums">
                            {line.percent !== null ? (
                              `${line.percent}%`
                            ) : (
                              <span className="text-rose-600">—</span>
                            )}
                          </td>
                          <td className="py-2 pr-3 text-right font-mono tabular-nums">
                            {line.commission === '0.00' && line.percent === null ? (
                              <span className="text-rose-600">{formatPln(line.commission)}</span>
                            ) : (
                              formatPln(line.commission)
                            )}
                          </td>
                          <td className="py-2 pr-3">
                            <RuleScopeBadge scope={line.ruleScope} />
                          </td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function KpiCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}): ReactNode {
  return (
    <div
      className={`rounded-xl bg-white p-5 shadow-sm ring-1 ring-zinc-200 ${
        highlight ? 'bg-gradient-to-br from-brand-50 to-white' : ''
      }`}
    >
      <div className="text-sm font-medium text-zinc-500">{label}</div>
      <div
        className={`mt-2 text-2xl font-semibold ${
          highlight ? 'text-brand-700' : 'text-zinc-900'
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function RuleScopeBadge({ scope }: { scope: CommissionLine['ruleScope'] }): ReactNode {
  const { t } = useTranslation();
  if (scope === 'PER_SERVICE') {
    return (
      <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
        {t('commissions.payouts.scope.perService')}
      </span>
    );
  }
  if (scope === 'BASE') {
    return (
      <span className="rounded bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800">
        {t('commissions.payouts.scope.base')}
      </span>
    );
  }
  return (
    <span className="rounded bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-800">
      {t('commissions.payouts.scope.none')}
    </span>
  );
}
